import React, { JSX, useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { AccessControlKeys } from "utilities/enumerations/authorization/access-control-keys";
import { ActiveStatus } from "models/enumerations/active-status/active-status";
import { ButtonIcon } from "components/buttons/button-icon/button-icon";
import { ButtonSize, ButtonStyle } from "components/buttons/button/button";
import { CalendarMonthView } from "components/calendar-month-view/calendar-month-view";
import { CollectionUtils } from "utilities/collection-utils";
import { CreateEventModal } from "components/events/create-event-modal/create-event-modal";
import { EmptyText } from "components/empty-text/empty-text";
import { EventList } from "components/events/event-list/event-list";
import { EventType } from "models/enumerations/events/event-type";
import { HeaderBanner } from "components/header-banner/header-banner";
import { Icons } from "components/icons/constants/icons";

import { NumberUtils } from "utilities/number-utils";
import { Pager } from "components/pager/pager";
import {
    Paragraph,
    ParagraphSize,
    ParagraphStyle,
} from "components/typography/paragraph/paragraph";
import { ProductService } from "utilities/services/products/product-service";
import { ProviderRecord } from "models/view-models/providers/provider-record";
import {
    ProviderResourcePathParams,
    ProviderService,
} from "utilities/services/providers/provider-service";

import {
    RadioButtonGroup,
    RadioButtonGroupOption,
} from "components/radio-button-group/radio-button-group";
import { RoleType } from "models/enumerations/users/role-type";
import { RouteUtils } from "utilities/route-utils";
import { ScrollUtils } from "utilities/scroll-utils";
import { SelectOption } from "components/form/inputs/select/select";
import { SkipNavContent } from "@chakra-ui/skip-nav";
import { ToastManager } from "utilities/toast/toast-manager";
import { TranslatedCopy } from "utilities/interfaces/culture-resources";
import { sitemap } from "sitemap";
import { t } from "utilities/localization/t";
import { useCalendarEvents } from "utilities/hooks/models/events/use-calendar-events";
import { useEvents } from "utilities/hooks/models/events/use-events";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import { useLocation, useNavigate } from "react-router";
import { useRedirectOnForbidden } from "utilities/hooks/aspects/authorization/use-redirect-on-forbidden";
import { useViewEventRoutes } from "utilities/hooks/models/events/use-view-event-routes";
import { validatePageAccess } from "utilities/decorators/aspects/authorization/validate-page-access";
import { useEventSearch } from "utilities/contexts/search/event/event-search-context";
import AdminEventFilterComponent from "../admin-event-filter-component/admin-event-filter-component";
import "./admin-event-list-page.scss";

// -------------------------------------------------------------------------------------------------
// #region Enums
// -------------------------------------------------------------------------------------------------

export enum AdminDashboardTrainingViewTypes {
    Calendar = "1",
    List = "2",
}

// #endregion Enums

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AdminEventListPageProps {}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME = "admin-event-list-page";
const ITEMS_PER_PAGE = 20;
const TRAINING_VIEW_OPTIONS: SelectOption[] = [
    { value: AdminDashboardTrainingViewTypes.List, text: "listView" as TranslatedCopy },
    { value: AdminDashboardTrainingViewTypes.Calendar, text: "calendarView" as TranslatedCopy },
];

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AdminEventListPage: React.FC<AdminEventListPageProps> = validatePageAccess(
    AccessControlKeys.AdminEventListPage
)((): JSX.Element => {
    useRedirectOnForbidden(sitemap.public.noAccess);

    const { search } = useLocation();
    const incomingOrganizationIdParam = new URLSearchParams(search).get("organizationId");
    const incomingProviderIdParam = new URLSearchParams(search).get("providerId");

    const { initialCriteria, setCriteria } = useEventSearch();
    const [openCreateEventModal, setOpenCreateEventModal] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState<moment.Moment>(moment().startOf("month"));
    const [currentTrainingsViewType, setCurrentTrainingsViewType] =
        useState<AdminDashboardTrainingViewTypes>(AdminDashboardTrainingViewTypes.List);
    const [calendarToggleIndex, setCalendarToggleIndex] = useState(0);
    const { record: globalState } = useGlobalState();
    const isAEN = globalState.currentIdentity?.isCurrentlyInRole(RoleType.AenAdministrator);
    // Set the -1 to ensure event retrieval fails if the provider ID isn't set.
    const loggedInUserProviderId = useMemo(
        () => globalState?.currentIdentity?.user?.providerId ?? -1,
        [globalState?.currentIdentity?.user?.providerId]
    );
    const { get: getProvider } = ProviderService.useGet();
    const [associatedProvider, setAssociatedProvider] = useState<ProviderRecord>();
    const currentUserIsInNfpaRole = globalState.currentIdentity?.isCurrentlyInNfpaRole() ?? false;
    const canEditEvents =
        ((isAEN && associatedProvider?.isActive) ||
            (currentUserIsInNfpaRole &&
                !globalState.currentIdentity?.isCurrentlyInRole(RoleType.NfpaSupport))) ??
        false;
    const navigate = useNavigate();
    const [products, setProducts] = useState<SelectOption[]>([]);
    const { list: listProducts } = ProductService.useList();

    const fetchProvider = useCallback(async () => {
        try {
            const pathParams: ProviderResourcePathParams = {
                id: loggedInUserProviderId,
            };

            const providerResponse = await getProvider(pathParams);
            const providerResult = providerResponse?.result;

            if (providerResult?.resultObject == null || providerResult.hasErrors()) {
                throw new Error();
            }
            setAssociatedProvider(providerResult.resultObject);
        } catch {
            ToastManager.error(t("problemLoadingProvider"));
        }
    }, [getProvider, loggedInUserProviderId]);

    const fetchProducts = useCallback(async () => {
        try {
            const listProductsResponse = await listProducts({
                activeStatusFilter: [ActiveStatus.Active],
                trainingType: 0,
            });

            if (
                listProductsResponse?.resultObjects == null ||
                listProductsResponse.results == null ||
                listProductsResponse.results.hasErrors()
            ) {
                throw new Error();
            }

            const products = listProductsResponse.resultObjects;
            const productSelectOptions = products
                .map((product) => {
                    return {
                        text: product.name,
                        value: product.id!.toString(),
                    };
                }, [])
                .sort((a, b) => (a.text.toLowerCase() < b.text.toLowerCase() ? -1 : 1));
            
            productSelectOptions.unshift({ text: "", value: "" });
            setProducts(productSelectOptions);
        } catch {
            ToastManager.error(t("problemLoadingProducts"));
            setProducts([]);
        }
    }, [listProducts]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    useEffect(() => {
        if (isAEN) {
            fetchProvider();
        }
    }, [fetchProvider, isAEN]);

    const addOneDay = (date: Date) => {
        return moment(date).add(1, "d");
    };

    const providerAssigned = useMemo(
        () => (initialCriteria.selectedProvider === "Unassigned" ? false : undefined),
        [initialCriteria.selectedProvider]
    );

    const [initialParamsSet, setInitialParamsSet] = useState(false);

    const providerSearchId = useMemo(() => {
       if (!initialParamsSet) {
            setInitialParamsSet(true);
        
            if (incomingProviderIdParam != null && incomingProviderIdParam !=="") {
                setCriteria((prev) => ({
                    ...prev,
                    selectedProvider: incomingProviderIdParam ?? prev.selectedProvider,
                }));
                return Number.parseInt(incomingProviderIdParam);
            }
        }
        return initialCriteria.selectedProvider ? Number.parseInt(initialCriteria.selectedProvider) : undefined;
    }, [initialCriteria.selectedProvider]);

    const organizationAssigned = useMemo(
        () => (initialCriteria.selectedOrganization === "Unassigned" ? false : undefined),
        [initialCriteria.selectedOrganization]
    );

    const organizationSearchId = useMemo(() => {
        if (!initialParamsSet) {
            setInitialParamsSet(true);
        
            if (incomingOrganizationIdParam != null && incomingOrganizationIdParam !=="") {
                setCriteria((prev) => ({
                    ...prev,
                    selectedOrganization: incomingOrganizationIdParam ?? prev.selectedOrganization,
                }));
                return Number.parseInt(incomingOrganizationIdParam);
            }
        }
        return initialCriteria.selectedOrganization ? Number.parseInt(initialCriteria.selectedOrganization) : undefined;
    }, [initialCriteria.selectedOrganization]);

    const { events, rowCount: totalNumberOfEvents } = useEvents({
        applySearch: true,
        endDate: initialCriteria.endDate ? addOneDay(initialCriteria.endDate).toISOString().split("T")[0] : "",
        eventSalesType: initialCriteria.eventSalesType,
        instructorLedTrainingType: initialCriteria.instructorLedTrainingType,
        organizationAssigned: organizationAssigned,
        organizationId: organizationSearchId ?? undefined,
        preventLoadEvents: currentTrainingsViewType === AdminDashboardTrainingViewTypes.Calendar,
        productId: initialCriteria.productId,
        providerAssigned: providerAssigned,
        providerId: isAEN ? loggedInUserProviderId : providerSearchId,
        searchText: initialCriteria.searchText,
        skip: (initialCriteria.currentPage - 1) * ITEMS_PER_PAGE,
        startDate: initialCriteria.startDate ? initialCriteria.startDate.toISOString().split("T")[0] : "",
        status: initialCriteria.status,
        take: ITEMS_PER_PAGE,
    });

    const calendarToggleOptions: RadioButtonGroupOption[] = [
        {
            id: "all-events",
            index: 0,
            text: t("allEvents"),
        },
        {
            id: "contracts",
            index: 1,
            salesType: EventType.Contract,
            text: t("contracts"),
        },
        {
            id: "educationNetwork",
            index: 3,
            salesType: EventType.EducationNetwork,
            text: t("educationNetwork"),
        },
        {
            id: "nfpa",
            index: 2,
            salesType: EventType.NFPACatalog,
            text: t("nfpa"),
        },
    ];

    const { events: calendarEvents } = useCalendarEvents({
        calendarIsHidden: currentTrainingsViewType === AdminDashboardTrainingViewTypes.List,
        eventSalesType: calendarToggleOptions[calendarToggleIndex].salesType,
        month: calendarMonth.toDate().getMonth(),
        year: calendarMonth.toDate().getFullYear(),
    });

    useEffect(() => {
        ScrollUtils.scrollToElementBySelector(`.${CSS_CLASS_NAME}__window__main__events__content`);
    }, [events]);

    const onPageClick = (pageNumber: number) => {
        setCriteria((prev) => ({ ...prev, currentPage: pageNumber }));
    };
    
    const handleCreateEventModalClose = (): void => {
        setOpenCreateEventModal(false);
    };

    const handleNextMonthClick = () => setCalendarMonth(calendarMonth.clone().add(1, "month"));
    const handlePreviousMonthClick = () =>
        setCalendarMonth(calendarMonth.clone().subtract(1, "month"));



    const onCalendarToggle = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setCurrentTrainingsViewType(e.currentTarget.value as AdminDashboardTrainingViewTypes);
    };

    const { viewEventRoutes } = useViewEventRoutes({
        roleType: globalState?.currentIdentity?.role?.roleType,
    });

    const getEditEventPath = (eventId: number) => {
        const path = currentUserIsInNfpaRole
            ? sitemap.admin.event.edit.details
            : sitemap.admin.event.edit.contact;

        return RouteUtils.replacePathParams(path, { id: eventId });
    };

    const getViewEventPath = (eventId: number) => {
        return RouteUtils.replacePathParams(viewEventRoutes.details, {
            id: eventId,
        });
    };

    const handleEditEventClick = (eventId: number) => {
        navigate(getEditEventPath(eventId));
    };

    const handleViewEventClick = (eventId: number) => {
        navigate(getViewEventPath(eventId));
    };

    return (
        <>
            <div className={CSS_CLASS_NAME}>
                <HeaderBanner
                    title={t("events")}
                    viewItems={TRAINING_VIEW_OPTIONS}
                    viewOnChange={onCalendarToggle}
                    currentViewType={currentTrainingsViewType}>
                    {canEditEvents && (
                        <ButtonIcon
                            text={t("addNewEvent")}
                            disabled={isAEN && loggedInUserProviderId === -1}
                            onClick={() => setOpenCreateEventModal(true)}
                            buttonSize={ButtonSize.Medium}
                            buttonStyle={ButtonStyle.TertiaryAlt}
                            iconType={Icons.Plus}
                        />
                    )}
                </HeaderBanner>

                <div className={`${CSS_CLASS_NAME}__window`}>
                    {currentTrainingsViewType === AdminDashboardTrainingViewTypes.List && (
                        <div className={`${CSS_CLASS_NAME}__list__view`}>
                            <div className={`${CSS_CLASS_NAME}__window__sidebar`}>
                                <div className={`${CSS_CLASS_NAME}__window__sidebar__filter`}>
                                    <Paragraph
                                        cssClassName={`${CSS_CLASS_NAME}__window__sidebar__filter__title`}
                                        size={ParagraphSize.XLarge}
                                        style={ParagraphStyle.Label}>
                                        {t("filterResults")}
                                    </Paragraph>

                                    <div
                                        className={`${CSS_CLASS_NAME}__window__sidebar__filter__selections`}>
                                        <AdminEventFilterComponent isAEN={isAEN} products={products} />
                                    </div>
                                </div>
                            </div>
                            <div className={`${CSS_CLASS_NAME}__window__main`}>
                                <div className={`${CSS_CLASS_NAME}__window__main__events`}>
                                    <div
                                        className={`${CSS_CLASS_NAME}__window__main__events__content`}>
                                        <SkipNavContent>
                                            {CollectionUtils.hasValues(events) ? (
                                                <EventList events={events} />
                                            ) : (
                                                <EmptyText table>
                                                    {t("noEventResultsFound")}
                                                </EmptyText>
                                            )}
                                        </SkipNavContent>
                                    </div>
                                </div>
                                {CollectionUtils.hasValues(events) && (
                                    <div className={`${CSS_CLASS_NAME}__window__main__pager`}>
                                        <Pager
                                            currentPage={initialCriteria.currentPage}
                                            totalPages={totalNumberOfEvents / ITEMS_PER_PAGE}
                                            onPageClick={onPageClick}
                                            itemsPerPage={ITEMS_PER_PAGE}
                                            totalItems={totalNumberOfEvents}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {currentTrainingsViewType === AdminDashboardTrainingViewTypes.Calendar && (
                        <div className={`${CSS_CLASS_NAME}__calendar__view`}>
                            <div>
                                {!isAEN && (
                                    <div className={`${CSS_CLASS_NAME}__calendar__toggles`}>
                                        {calendarToggleOptions.map((button, idx) => (
                                            <RadioButtonGroup
                                                key={`${button.id}-${idx}`}
                                                ariaLabelledBy={button.id}
                                                first={idx === 0}
                                                last={idx === calendarToggleOptions.length - 1}
                                                onClick={() => setCalendarToggleIndex(idx)}
                                                selected={calendarToggleIndex === idx}
                                                option={button}
                                            />
                                        ))}
                                    </div>
                                )}
                                <div className={`${CSS_CLASS_NAME}__calendar__wrapper`}>
                                    <CalendarMonthView
                                        events={calendarEvents}
                                        onEditEventClick={
                                            canEditEvents
                                                ? (eventId: number) => handleEditEventClick(eventId)
                                                : undefined
                                        }
                                        onNextMonthClick={handleNextMonthClick}
                                        onPreviousMonthClick={handlePreviousMonthClick}
                                        onViewEventClick={(eventId: number) =>
                                            handleViewEventClick(eventId)
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <CreateEventModal
                onModalClose={handleCreateEventModalClose}
                open={openCreateEventModal}
            />
        </>
    );
});

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AdminEventListPage };

// #endregion Exports
